import {ChangeDetectionStrategy, Component, TemplateRef, ViewChild} from '@angular/core';
import lodash from "lodash";
import {
  BulkRedeclareCommercialReleases,
  CancelCommercialRelease,
  CommercialReleaseSummary,
  FacetValueResult,
  FindCommercialReleaseSummaries,
  InlandOperator,
  RedeclareCommercialReleaseData,
  ReleaseToParty,
  VisitSummary
} from "@portbase/bezoekschip-service-typescriptmodels";
import {combineLatest, Observable, of} from "rxjs";
import {ActivatedRoute, Router} from "@angular/router";
import {AppContext} from "../../app-context";
import {QuickView} from "../../new-portvisit/common/facets/facet-filter/facet-filter.component";
import {DataQuery, FacetedOverview} from "../../new-portvisit/common/overview/faceted-overview";
import {FacetedOverviewFilters} from "../../new-portvisit/common/overview/overview.component";
import {Handler} from "../../common/handlers/handler";
import {HandleQuery} from "../../common/handlers/handle-query";
import {
  nonNull,
  openConfirmationModalWithCallback,
  openEditModal,
  sendCommand,
  sendCommandAndWait
} from "../../common/utils";
import {SentenceCasePipe} from "../../common/sentence-case.pipe";
import {PortvisitUtils} from "../../refdata/portvisit-utils";
import {DateFieldRange} from "../../common/date/date-range/date-field-range";
import {ConsignmentUtils} from "../../new-portvisit/consignments/consignment.utils";
import {
  CommercialReleaseModalComponent,
  CommercialReleaseModalComponentData
} from "./commercial-release-modal/commercial-release-modal.component";
import {exportDataAsExcel} from "../../common/upload/excel.utils";
import {ModalConfirmAutofocus, ModalConfirmAutofocusData} from "../../common/modal/modal-confirm.component";
import {
  CommercialReleaseUploadModalComponent
} from "./commercial-release-upload-modal/commercial-release-upload-modal.component";
import {
  findReleaseToParties,
  terminalOrganisationName,
  terminals
} from "../../commercial-release/commercial-release.utils";

const quickViews: QuickView[] = [{
  code: "ALL_RELEASES",
  name: "All releases",
  icon: "fa-file-invoice-dollar",
  facets: {}
}, {
  code: "ATTENTION_REQUIRED",
  name: "Attention required",
  icon: "fa-circle-exclamation",
  facets: {},
  queryFn: (query: DataQuery<FindCommercialReleaseSummaries>): DataQuery<FindCommercialReleaseSummaries> => {
    return ({
      ...query,
      payload: {
        ...query.payload,
        attentionRequired: true
      }
    });
  }
}];

/**
 * Phase 1 read-only list skeleton for the new commercial-release overview. Both quick views are
 * server-side: `ALL_RELEASES` runs the plain `FindCommercialReleaseSummaries` query, and
 * `ATTENTION_REQUIRED` sets the real `attentionRequired: true` payload field via `queryFn` - same
 * `queryFn` pattern as `visit-overview.component.ts`'s own `ATTENTION_REQUIRED` quick view. There is
 * no client-side phase (`ReleasePhase`) filtering here.
 */
@Component({
  selector: 'app-commercial-release-overview',
  templateUrl: './commercial-release-overview.component.html',
  styleUrls: ['./commercial-release-overview.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
@Handler()
export class CommercialReleaseOverviewComponent extends FacetedOverview<CommercialReleaseSummary, CommercialReleaseOverviewFilters> {
  appContext = AppContext;
  protected readonly utils = ConsignmentUtils;
  protected readonly refData = PortvisitUtils;

  // Selection gate matches the OLD screen's checkbox gate exactly (isCommercialReleaseDeclarant() ||
  // isCommercialReleaseEditor(), commercial-release-overview.component.html). The OLD screen does NOT
  // exclude already-cancelled releases from being selectable/checkable - the checkbox renders for
  // every row regardless of `releaseStatus?.cancelled`, and Export/Change terminal apply to any
  // selected row including cancelled ones. Only `cancelSelected()` filters `!releaseStatus?.cancelled`
  // out of ITS OWN input, as a per-action guard against double-cancelling - so that filter is kept
  // there (ported below) rather than folded into `isSelectable`, which would incorrectly also hide
  // cancelled rows from Export/Change terminal.
  isSelectable = (record: CommercialReleaseSummary) =>
    AppContext.isCommercialReleaseDeclarant() || AppContext.isCommercialReleaseEditor();

  get anyRecordSelectable(): boolean {
    return this.data?.length && this.data.some(d => this.isSelectable(d));
  }

  protected readonly terminals = terminals;
  protected readonly terminalOrganisationName = terminalOrganisationName;
  redeclareData: RedeclareCommercialReleaseData = {};
  @ViewChild("bulkRedeclareBody") bulkRedeclareBody: TemplateRef<any>;

  constructor(route: ActivatedRoute, private router: Router) {
    // With `DECLARED_REJECTED` removed, the choice is between `ALL_RELEASES` and the new
    // `ATTENTION_REQUIRED`. The founder's phrasing ("we can use this later for releases the customer
    // still needs to act on") reads as "add the capability now, use it later" rather than "make it the
    // default view" - so `ALL_RELEASES` (the first quick view) stays the default, same as
    // `quickViews[0]` used by the empty-state "Try going to the All releases quick view" link below.
    // `localStorageKey` is passed as `undefined` here (not the literal string) - same as
    // consignments'/containers' constructors - because the real key is only set conditionally below,
    // once we know whether this is a CRN-scoped view.
    super(undefined, quickViews, quickViews.find(q => q.code === "ALL_RELEASES"));
    route.params.subscribe(params => {
      route.queryParams.subscribe(_queryParams => {
        this.filters.overviewFilters.filters.crn = params["crn"];
        if (this.filters.overviewFilters.filters.crn) {
          this.filters.overviewFilters.selectedQuickView = this.quickViews[0];
        } else {
          this.setLocalStorageKey("commercial-release-overview-new-filters");
        }
      });
    });
  }

  getDataQuery = (term: string): DataQuery<FindCommercialReleaseSummaries> => {
    if (!this.filters.overviewFilters.filters.timeRange) {
      this.updateTimeRangeToDefault(term);
    }
    const timeRange = this.prepareDateTimeRange(this.filters.overviewFilters.filters.timeRange,
      !term, this.filters.overviewFilters.filters.timeRange);
    const queryFn = this.filters.overviewFilters.selectedQuickView?.queryFn;
    const query = <DataQuery<FindCommercialReleaseSummaries>>{
      className: "com.portbase.bezoekschip.common.api.commercialrelease.FindCommercialReleaseSummaries",
      payload: {
        term: term,
        crn: this.filters.overviewFilters.filters.crn,
        releaseToPartyPortbaseId: this.filters.overviewFilters.filters.releaseToParty?.portbaseId,
        inlandOperatorEan: this.filters.overviewFilters.filters.inlandOperator?.ean,
        dateTimeRange: term || this.filters.overviewFilters.filters.crn ? null : timeRange,
        declarantShortName: AppContext.isAdmin() ? null : AppContext.userProfile.organisation?.shortName,
        pagination: {from: this.from, count: this.maxItems},
        facetFilters: this.getFacetFilters()
      }
    };
    return queryFn ? queryFn(query) : query;
  }

  renderRecords = (result: CommercialReleaseSummary[], append: boolean) => {
    this.saveFiltersInLocalStorage(this.filters);
    if (result.length < this.maxItems) {
      this.endReached = true;
    }
    this.data = append ? (this.data || []).concat(result) : result;
  }

  trackByRecord = (index: number, record: CommercialReleaseSummary): any => record.id;

  openNewRelease = () => {
    openEditModal(CommercialReleaseModalComponent, <CommercialReleaseModalComponentData>{release: null}, {
      warnOnClose: true
    });
  }

  protected crnFilterValueFormatter = (visit: VisitSummary | string) =>
    (visit as VisitSummary)?.crn || (visit as string);

  protected crnFilterChanged = (visit: VisitSummary | string) => {
    this.filters.overviewFilters.filters.crn = this.crnFilterValueFormatter(visit);
    this.router.navigateByUrl(`/commercial-release-new${this.filters.overviewFilters.filters.crn ? '/' + this.filters.overviewFilters.filters.crn : ''}`)
  }

  findVisits = (term) => ConsignmentUtils.findVisits(term, null);

  // Release-to party facet filter, per the founder's KT-backend addition of
  // `FindCommercialReleaseSummaries.releaseToPartyPortbaseId`. Mirrors the CRN search field's
  // shape (findX/xFilterValueFormatter/xFilterChanged), but does NOT navigate/URL-scope like
  // `crnFilterChanged` does - that side effect is CRN-specific (no precedent/requirement for this
  // field). `ownerShortName` mirrors `getDataQuery()`'s own `declarantShortName` scoping: admins get
  // an unscoped search, everyone else is scoped to their own org.
  findReleaseToPartiesForFacet = (term: string): Observable<ReleaseToParty[]> =>
    findReleaseToParties(term, AppContext.isAdmin() ? null : AppContext.userProfile.organisation?.shortName);

  protected releaseToPartyFilterValueFormatter = (party: ReleaseToParty | string) =>
    (party as ReleaseToParty)?.name || (party as string);

  protected releaseToPartyFilterChanged = (party: ReleaseToParty | string) => {
    // Send the customer's `portbaseId` specifically (per the founder's explicit instruction), not
    // the party's name or the whole object.
    this.filters.overviewFilters.filters.releaseToParty = party as ReleaseToParty;
    // Unlike `crnFilterChanged`, there is no dedicated route slug for this field, so a reload can't
    // ride along on a route-driven component recreation. Trigger the reload explicitly instead, via
    // the same `loadData()` the checkbox sidebar facets use (`FacetedOverview.facetFiltersChanged()`).
    // `loadData()`'s default `append=false` already resets `from`/`endReached` and persists filters
    // (through `getData()`'s own `saveFiltersInLocalStorage` call), so no extra bookkeeping is needed
    // here.
    this.loadData();
  }

  // Inland operator search filter, mirroring the release-to-party filter above exactly. Unlike
  // `findReleaseToPartiesForFacet`, there is no org-scoping branch here: `FindInlandOperatorsByEanCode`
  // (typescript-models/common.d.ts) takes only `term` - no `ownerShortName`-equivalent parameter exists
  // to pass, confirmed against the generated model.
  findInlandOperatorsForFacet = (term: string): Observable<InlandOperator[]> =>
    PortvisitUtils.findInlandOperatorsByEanCode(term);

  protected inlandOperatorFilterValueFormatter = (operator: InlandOperator | string) =>
    (operator as InlandOperator)?.name || (operator as string);

  protected inlandOperatorFilterChanged = (operator: InlandOperator | string) => {
    this.filters.overviewFilters.filters.inlandOperator = operator as InlandOperator;
    this.loadData();
  }

  // `count` is the total-count facet (rendered via `releaseCount` below, not as a sidebar filter).
  // `crn` has hundreds of distinct values (441 in a real GetFacets sample) - unusable as a dropdown
  // filter, and redundant with the free-text `term` search plus the per-row CRN already shown.
  // `allowedViewers` is an internal access-control field (org codes with system-generated visibility),
  // not a user-facing concept - same precedent as consignments' own `getHiddenFacets()` hiding it.
  // `declarantShortName` stays HIDDEN too: for a non-admin, `getDataQuery()` already scopes the query to
  // their own org via the `declarantShortName` payload field, so a same-named facet filter would be a
  // no-op/confusing duplicate; for an admin it could be a legitimate cross-org filter, but consignments'
  // equivalent (`declarantName`) is likewise hidden for non-admins only. Since this facet is org-code
  // based (same shape as containerOperatorShortName, which we DO show) and there's no dedicated need
  // signalled for a declarant filter distinct from the container-operator one, keep it hidden for
  // everyone for now - it can be unhidden for admins later if a real need shows up.
  // `declarationStatus`, `containerOperatorShortName` and `releaseData/releaseModality` stay VISIBLE -
  // genuine, low-cardinality, user-facing filters ("declaration status" / "carrier" / "modality").
  // `empty` is a flat facet on `CommercialReleaseSummary` (not nested under `releaseData`) with no
  // current user-facing value - hidden per the founder's explicit correction (it "may stay hidden for
  // now").
  getHiddenFacets = (): string[] => ["count", "crn", "allowedViewers", "declarantShortName", "empty"];

  get releaseCount(): number {
    return lodash.sum(this.allFacets?.find(f => f.name === "count")?.values
      .map(v => v.count)) || 0;
  }

  nameFormatter = (name: string): string => {
    switch (name) {
      case "declarationStatus":
        return "Declaration status";
      case "containerOperatorShortName":
        return "Container operator";
      case "releaseData/releaseModality":
        return "Modality";
      case "releaseData/terminalShortName":
        return "Terminal";
      default:
        return SentenceCasePipe.formatWithSpaces(name);
    }
  }

  // `declarationStatus` and `releaseData/releaseModality` need value formatting: their raw enum
  // values (ACCEPTED/DECLARED/REJECTED/SCHEDULED, resp. road/rail/barge/sea) read as shouty- or
  // lower-case in the facet sidebar, same problem `HarbourDuesOverviewComponent.getValueFormatter`
  // solves for its own status facets via `PortvisitUtils.enumFormatter` ("Accepted"/"Declared"/etc).
  // `enumFormatter` (title-cases + replaces underscores) reads perfectly fine for the single-word
  // modality values too ("road" -> "Road", "barge" -> "Barge", "rail" -> "Rail", "sea" -> "Sea"), so
  // no bespoke mapping is needed there. `containerOperatorShortName`'s values are org codes (e.g.
  // "HAPAGLLOYD") that don't map to a nicer label, so it's left unformatted - safe now that
  // faceted-overview.ts guards a missing `getValueFormatter`.
  getValueFormatter = (name: string): (value: string) => string => {
    switch (name) {
      case "declarationStatus":
      case "releaseData/releaseModality":
        return PortvisitUtils.enumFormatter;
      case "releaseData/terminalShortName":
        return terminalOrganisationName;
      default:
        return null;
    }
  }

  @HandleQuery("getFacetValues", { global: true })
  getFacetValues(facetName: string): Observable<FacetValueResult[]> {
    if (Object.keys(this.filters.overviewFilters.facets).length) {
      return super.getFacetValues(facetName);
    }
    return of(this.allFacets.find(f => f.name === facetName).values);
  }

  /*
    Upload, redesigned as a drag-and-drop popup modal - see
    .kiro/agents/tasks/commercial-release-upload-modal-redesign.md. The modal owns its own
    parse/send flow (CommercialReleaseUploadModalComponent) and only reports back whether the upload
    actually succeeded; on success the overview's own data is reloaded here, via the confirm callback.
   */

  uploadCommercialReleases = () => {
    openConfirmationModalWithCallback((confirmed) => {
      if (confirmed) {
        this.loadData();
      }
    }, CommercialReleaseUploadModalComponent, {modalSize: 'lg'}, 'static');
  }

  /*
    Bulk actions, ported from the OLD screen's commercial-release-overview.component.ts, re-pointed
    at the immutable selectedIds-based selection (isRecordSelected/data.filter) instead of the OLD
    screen's mutable `selectedItems` getter.
   */

  getSelectedReleases(): CommercialReleaseSummary[] {
    return (this.data || []).filter(r => this.isRecordSelected(r));
  }

  get nrOfSelectedReleases(): number {
    return this.getSelectedReleases().length;
  }

  cancelSelected() {
    // Same per-action guard the OLD screen's cancelSelected() applies: already-cancelled releases are
    // excluded here rather than from isSelectable/the checkbox itself (see isSelectable's comment).
    combineLatest(this.getSelectedReleases().filter(i => !i.releaseStatus?.cancelled)
      .map(i => sendCommandAndWait('com.portbase.bezoekschip.common.api.commercialrelease.CancelCommercialRelease',
        <CancelCommercialRelease>{id: i.id})))
      .subscribe({
        next: () => {
          AppContext.registerSuccess('The releases were cancelled successfully');
          this.clearSelection();
          this.loadData();
        },
        error: error => AppContext.registerError(error)
      });
  }

  exportSelected() {
    exportDataAsExcel(this.buildExportRows(this.getSelectedReleases()), 'releases.xlsx');
  }

  // Split out from exportSelected() purely so the exact row shape (column order/fields) can be
  // asserted directly in a unit test without depending on the xlsx-js-style library internals.
  buildExportRows(releases: CommercialReleaseSummary[]): string[][] {
    const header = ["Container number", "Consignment number", "Terminal", "Carrier",
      "Release-to party", "Declaration date", "Status"];
    const data = [header];
    releases.forEach(value => {
      const entry: string[] = [value.releaseData.equipmentNumber, value.releaseData.consignmentNumber,
        value.releaseData.terminalShortName, value.releaseData.containerOperator?.name,
        value.releaseData.releaseToParty?.name, value.releaseStatus?.declarationDate, value.releaseStatus?.status];
      data.push(entry);
    });
    return data;
  }

  redeclareCommercialReleases() {
    openConfirmationModalWithCallback((confirmed) => {
      if (confirmed) {
        const ids = this.getSelectedReleases().map(s => s.id).filter(nonNull);
        sendCommand("com.portbase.bezoekschip.common.api.commercialrelease.BulkRedeclareCommercialReleases", <BulkRedeclareCommercialReleases>{
          ids: ids,
          redeclareData: this.redeclareData
        }, () => {
          AppContext.registerSuccess(`Sucessfully changed the terminal of ${ids.length} commercial releases`);
          this.redeclareData = {};
          this.clearSelection();
          this.loadData();
        });
      }
    }, ModalConfirmAutofocus, <ModalConfirmAutofocusData>{
      type: "primary",
      title: "Change terminal",
      confirmText: "Send",
      cancelText: "Cancel",
      modalSize: "lg",
      body: this.bulkRedeclareBody
    }, 'static');
  }
}

export interface CommercialReleaseOverviewFilters extends FacetedOverviewFilters<CommercialReleaseOverviewFacetFilters> {
}

export interface CommercialReleaseOverviewFacetFilters {
  timeRange?: DateFieldRange;
  crn?: string;
  releaseToParty?: ReleaseToParty;
  inlandOperator?: InlandOperator;
}
